// The app home (every app in the server as a grid, "New app" and "New bot", and the Blooming section) and an app's page (its full
// view, its data History, and About: owner, editors, versions, its blooming thread).
import { useEffect, useState } from "react";
import { INTEGRATIONS, type AppEvent, type AppManifest, type BloomingThread, type BotInfo, type IntegrationSetting, type Member, type Role } from "@bloom/shared";
import type { Room } from "../realtime.ts";
import { Avatar, colorFor } from "../components/ui.tsx";
import { AppFrame } from "../components/AppFrame.tsx";
import { AppAbout, BloomingList } from "./Blooming.tsx";
import "../components/apps.css";

export function AppsHome({ apps, blooming, names, canBloom, onOpen, onOpenThread, onNew, integrations, onIntegrations }: {
  apps: AppManifest[];
  /** What's on in this server, for the Integrations entry. */
  integrations: IntegrationSetting[];
  onIntegrations: () => void;
  blooming: BloomingThread[];
  names: Map<string, string>;
  canBloom: boolean;
  onOpen: (slug: string) => void;
  onOpenThread: (id: string) => void;
  /** "New app" or "New bot": separate entry points, since an app never becomes a bot (docs/bots.md). */
  onNew: (kind: "app" | "bot") => void;
}) {
  return (
    <div className="apps-home">
      <div className="apps-home-head">
        <div>
          <h2>Apps in this server</h2>
          <p className="muted">Use one from any conversation with <kbd>/</kbd>, or open it here.</p>
        </div>
        {canBloom && (
          <span className="apps-home-new">
            <button type="button" className="btn btn-primary" onClick={() => onNew("app")}>
              <span aria-hidden="true">🌱</span> New app
            </button>
            <button type="button" className="btn" onClick={() => onNew("bot")}>
              <span aria-hidden="true">🤖</span> New bot
            </button>
          </span>
        )}
      </div>
      <ul className="app-grid" aria-label="Apps">
        {apps.map((a) => (
          <li key={a.slug}>
            <button type="button" className="app-tile" onClick={() => onOpen(a.slug)}>
              <span className="app-icon" aria-hidden="true">{a.icon}</span>
              <span className="app-tile-text">
                <span className="app-tile-name">{a.name}</span>
                <span className="app-tile-desc">{a.description}</span>
                <span className="app-tile-meta">
                  /{a.slug}
                  {a.firstParty ? " · by Bloom" : a.bloom ? <span className="app-tile-by"> · by {names.get(a.bloom.owner) ?? "someone"}</span> : null}
                </span>
              </span>
            </button>
          </li>
        ))}
        {canBloom && (
          <li>
            <button type="button" className="app-tile new-tile" onClick={() => onNew("app")}>
              <span className="app-icon" aria-hidden="true">🌱</span>
              <span className="app-tile-text">
                <span className="app-tile-name">Bloom a new app</span>
                <span className="app-tile-desc">Describe it, and build it together in a thread.</span>
              </span>
            </button>
          </li>
        )}
        {canBloom && (
          <li>
            <button type="button" className="app-tile new-tile" onClick={() => onNew("bot")}>
              <span className="app-icon" aria-hidden="true">🤖</span>
              <span className="app-tile-text">
                <span className="app-tile-name">Make a new bot</span>
                <span className="app-tile-desc">Your own bot that lives in chat. You build it in private, then add it to channels.</span>
              </span>
            </button>
          </li>
        )}
      </ul>
      <IntegrationsEntry settings={integrations} onOpen={onIntegrations} />
      <BloomingList threads={blooming} names={names} onOpen={onOpenThread} />
    </div>
  );
}

/** The app home's way to Apps → Integrations: what apps can reach outside Bloom, and how many are on. */
function IntegrationsEntry({ settings, onOpen }: { settings: IntegrationSetting[]; onOpen: () => void }) {
  const ready = INTEGRATIONS.filter((i) => settings.find((s) => s.id === i.id)?.ready);
  const waiting = settings.filter((s) => s.enabled && !s.ready).length;
  return (
    <section className="apps-integrations-entry" aria-labelledby="apps-int-head">
      <button type="button" className="app-tile int-tile" onClick={onOpen}>
        <span className="app-icon" aria-hidden="true">🔌</span>
        <span className="app-tile-text">
          <span className="app-tile-name" id="apps-int-head">Integrations</span>
          <span className="app-tile-desc">Outside data apps can use: {INTEGRATIONS.map((i) => i.name).join(", ")}.</span>
          <span className="app-tile-meta">
            {ready.length ? `${ready.length} of ${INTEGRATIONS.length} on: ${ready.map((i) => `${i.icon} ${i.name}`).join(", ")}` : "None on yet"}
            {waiting ? ` · ${waiting} waiting for a key` : ""}
          </span>
        </span>
        <span className="int-tile-go" aria-hidden="true">›</span>
      </button>
    </section>
  );
}

interface PageProps {
  app: AppManifest;
  tab: "app" | "history" | "about";
  room: Room;
  me: { id: string; name: string; role: Role };
  members: Member[];
  names: Map<string, string>;
  route: Record<string, string>;
  channelId: () => string | null;
  onTab: (tab: "app" | "history" | "about") => void;
  openApp: (slug: string, route?: Record<string, string>) => void;
  /** Its blooming thread, if it was made with Bloom (and the thread still exists). */
  thread: BloomingThread | null;
  onOpenThread: (id: string) => void;
  /** Text channels, for where a built-in app's blooming thread goes ("Edit with Bloom"). */
  channels: { id: string; name: string }[];
  defaultChannel: string | null;
  /** The app's bot, if it is one (docs/bots.md). */
  bot?: BotInfo | null;
  onMessageBot?: () => void;
  onDeleted?: () => void;
}

export function AppPage({ app, tab, room, me, members, names, route, channelId, onTab, openApp, thread, onOpenThread, channels, defaultChannel, bot = null, onMessageBot, onDeleted }: PageProps) {
  // A bot's data is restored only by its owner; other apps' by their owner, editors and the server's owners and admins.
  const canRestore = app.bot ? app.bloom?.owner === me.id : me.role === "owner" || me.role === "admin" || (!!app.bloom && (app.bloom.owner === me.id || app.bloom.editors.includes(me.id)));
  const noView = !!app.bloom?.noView;
  // Opening its page: a bot with an appOpen trigger answers in our DM.
  useEffect(() => {
    if (tab === "app") room.appOpened(app.slug);
  }, [tab, app.slug, room]);
  return (
    <div className="app-page">
      <div className="app-page-head">
        <span className="app-icon small" aria-hidden="true">{app.icon}</span>
        <div className="app-page-title">
          <h2>{app.name}</h2>
          <div className="muted app-page-desc">{app.description}</div>
        </div>
        <div className="app-tabs" role="tablist" aria-label={`${app.name} pages`}>
          <button type="button" role="tab" aria-selected={tab === "app"} className={`app-tab${tab === "app" ? " active" : ""}`} onClick={() => onTab("app")}>App</button>
          <button type="button" role="tab" aria-selected={tab === "history"} className={`app-tab${tab === "history" ? " active" : ""}`} onClick={() => onTab("history")}>History</button>
          <button type="button" role="tab" aria-selected={tab === "about"} className={`app-tab${tab === "about" ? " active" : ""}`} onClick={() => onTab("about")}>About</button>
        </div>
      </div>
      <div className="app-page-body" role="tabpanel">
        {tab === "app" && noView ? (
          <div className="app-empty">
            <div className="app-empty-icon" aria-hidden="true">{bot?.avatar ?? app.icon}</div>
            <h2>{bot?.name ?? app.name} lives in chat</h2>
            <p className="muted">It has no page of its own: @mention it in a channel it's in, or message it directly.</p>
            {bot && onMessageBot && <button type="button" className="btn btn-primary" onClick={onMessageBot}>Message {bot.name}</button>}
          </div>
        ) : tab === "app" ? (
          <AppFrame
            app={app}
            room={room}
            user={me}
            members={() => members.map((m) => ({ id: m.id, name: m.name, color: colorFor(m.id) }))}
            channelId={channelId}
            route={route}
            openApp={openApp}
          />
        ) : tab === "history" ? (
          <History app={app} room={room} names={names} canRestore={canRestore} />
        ) : (
          <AppAbout app={app} room={room} me={me} members={members} names={names} thread={thread} onOpenThread={onOpenThread} channels={channels} defaultChannel={defaultChannel} bot={bot} {...(onMessageBot ? { onMessageBot } : {})} {...(onDeleted ? { onDeleted } : {})} />
        )}
      </div>
    </div>
  );
}

const when = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** The app's event log, readable ("Frank added “Dark mode”"), newest first, with Restore for owners and admins. */
function History({ app, room, names, canRestore }: { app: AppManifest; room: Room; names: Map<string, string>; canRestore: boolean }) {
  const [events, setEvents] = useState<AppEvent[] | null>(null);
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [note, setNote] = useState<{ id: number; text: string; error?: boolean } | null>(null);
  const load = async () => {
    const r = await room.appHistory(app.slug);
    if (r) {
      setEvents(r.events);
      setMore(r.more);
    }
  };
  useEffect(() => {
    void load();
    // Live: any change to the app's data (from anyone) brings the newest page again.
    const watches = Object.keys(app.collections).map((c) => room.watchCollection(app.slug, c));
    let t: number | undefined;
    const off = room.appData.onChange(app.slug, () => {
      clearTimeout(t);
      t = window.setTimeout(() => void load(), 150);
    });
    return () => {
      off();
      clearTimeout(t);
      for (const w of watches) w();
    };
  }, [app.slug, room]);
  const older = async () => {
    const last = events?.at(-1);
    if (!last) return;
    const r = await room.appHistory(app.slug, last.id);
    if (r) {
      setEvents([...(events ?? []), ...r.events]);
      setMore(r.more);
    }
  };
  const restore = async (ev: AppEvent) => {
    setBusy(ev.id);
    const r = await room.restore(app.slug, ev.id);
    setBusy(null);
    setNote({ id: ev.id, text: r.ok ? "Restored" : r.message, error: !r.ok });
    if (r.ok) void load();
  };
  if (!events) return <div className="history"><div className="history-empty muted">Loading…</div></div>;
  if (!events.length) {
    return (
      <div className="app-empty">
        <div className="app-empty-icon" aria-hidden="true">🕘</div>
        <h2>No history yet</h2>
        <p className="muted">Every change to the data in {app.name} shows up here, and can be restored.</p>
      </div>
    );
  }
  return (
    <div className="history">
      <p className="history-intro muted">Every change to the data in {app.name}, newest first.{canRestore ? " Restore puts something back the way it was right after that change (and is itself a change you can undo)." : ""}</p>
      <ol className="history-list">
        {events.map((ev) => {
          const who = names.get(ev.userId) ?? "Someone";
          return (
            <li key={ev.id} className={`history-item${ev.action === "restore" ? " restore" : ""}`} data-event={ev.id}>
              <Avatar id={ev.userId} name={who} size={28} />
              <div className="history-text">
                <div>
                  {ev.via ? (
                    // An AI acting for someone: "Bloom on behalf of Sam added …".
                    <span className="history-via" data-via={ev.via}><strong>{ev.via}</strong> on behalf of <strong>{who}</strong></span>
                  ) : (
                    <strong>{who}</strong>
                  )}{" "}
                  {ev.summary}
                </div>
                <time className="muted" dateTime={new Date(ev.at).toISOString()}>{when.format(ev.at)}</time>
              </div>
              {note?.id === ev.id && <span className={`history-note${note.error ? " error" : ""}`} role="status">{note.text}</span>}
              {canRestore && (
                <button type="button" className="btn btn-small history-restore" disabled={busy !== null} onClick={() => void restore(ev)} title="Put this back the way it was after this change">
                  {busy === ev.id ? "Restoring…" : "Restore"}
                </button>
              )}
            </li>
          );
        })}
      </ol>
      {more && <button type="button" className="link-btn history-more" onClick={() => void older()}>Show older changes</button>}
    </div>
  );
}
